import { useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { ArrowLeft, Download, Upload, CheckCircle, AlertCircle, AlertTriangle, X, FileSpreadsheet } from "lucide-react";
import { supabase } from "../supabaseClient";

// ── SCHEMA-EXACT MODULE DEFINITIONS ──────────────────────────────────────────
// Columns match exactly what Supabase inserts expect.
// Excluded: id (uuid, auto), company_id (injected at insert), created_at (auto)

const MODULES = {
  hedge: {
    id: "hedge",
    label: "Hedge Positions",
    table: "hedge_positions",
    icon: "🔒",
    color: "#6366f1",
    sheetName: "Hedge_Positions",
    // Dates in template: YYYY-MM-DD (Supabase date type)
    // Dual columns: notional_amount (text) + notional_amount_num (numeric), etc.
    columns: [
      { key: "contract_reference",  label: "Contract Reference",       required: true,  type: "text",    example: "FWD-2026-001",        note: "Unique reference" },
      { key: "instrument_type",     label: "Instrument Type",          required: true,  type: "text",    example: "Forward",              note: "Forward / Option (Call/Put) / Par Forward / Seagull / Range Forward / Participating Forward" },
      { key: "currency_pair",       label: "Currency Pair",            required: true,  type: "text",    example: "USD/INR",              note: "e.g. USD/INR, EUR/INR" },
      { key: "direction",           label: "Direction",                required: true,  type: "text",    example: "Buy",                  note: "Buy or Sell" },
      { key: "notional_amount_num", label: "Notional Amount",          required: true,  type: "numeric", example: "1000000",              note: "Number only, no commas" },
      { key: "rate_locked_num",     label: "Rate Locked",              required: false, type: "numeric", example: "83.50",                note: "Forward rate / locked rate" },
      { key: "strike_price",        label: "Strike Price",             required: false, type: "numeric", example: "84.00",                note: "For Options / Seagull" },
      { key: "barrier_high",        label: "Barrier High",             required: false, type: "numeric", example: "87.00",                note: "For Range Forward / Seagull" },
      { key: "barrier_low",         label: "Barrier Low",              required: false, type: "numeric", example: "81.00",                note: "For Range Forward / Seagull" },
      { key: "participation_rate",  label: "Participation Rate %",     required: false, type: "numeric", example: "50",                   note: "For Participating Forward" },
      { key: "option_type",         label: "Option Type",              required: false, type: "text",    example: "Call",                 note: "Call or Put — for Options only" },
      { key: "premium_paid_num",    label: "Premium Paid",             required: false, type: "numeric", example: "15000",                note: "Option premium amount" },
      { key: "counterparty_bank",   label: "Counterparty Bank",        required: true,  type: "text",    example: "HDFC Bank",            note: "" },
      { key: "trade_date",          label: "Trade Date",               required: true,  type: "date",    example: "2026-01-15",           note: "YYYY-MM-DD" },
      { key: "maturity_date",       label: "Maturity Date",            required: true,  type: "date",    example: "2026-06-30",           note: "YYYY-MM-DD" },
      { key: "expiry_date",         label: "Expiry Date",              required: false, type: "date",    example: "2026-06-30",           note: "YYYY-MM-DD — for Options" },
      { key: "window_start",        label: "Window Start",             required: false, type: "date",    example: "2026-06-01",           note: "YYYY-MM-DD — window forwards" },
      { key: "window_end",          label: "Window End",               required: false, type: "date",    example: "2026-06-30",           note: "YYYY-MM-DD — window forwards" },
      { key: "settlement_type",     label: "Settlement Type",          required: false, type: "text",    example: "Gross Settlement",     note: "Gross Settlement / Net Settlement" },
      { key: "status",              label: "Status",                   required: false, type: "text",    example: "Active",               note: "Defaults to Active if blank" },
      { key: "barrier_levels",      label: "Barrier Levels (text)",    required: false, type: "text",    example: "81.00 / 87.00",        note: "Free text description" },
      { key: 'underlying_type', label: 'Underlying Type', required: false, example: 'LC',          note: 'LC / BC / Receivable / Payable / Other' },
{ key: 'underlying_ref',  label: 'Underlying Ref',  required: false, example: 'LC-2024-001', note: 'Reference number of the underlying exposure' },
      { key: "notes",               label: "Notes",                    required: false, type: "text",    example: "",                     note: "" },
    ],
    // Derived fields to auto-populate at insert time
    derived: [
      // notional_amount (text) = String(notional_amount_num)
      { target: "notional_amount",   source: "notional_amount_num", transform: "toString" },
      // rate_locked (text) = String(rate_locked_num)
      { target: "rate_locked",       source: "rate_locked_num",     transform: "toString" },
      // premium_paid (text) = String(premium_paid_num)
      { target: "premium_paid",      source: "premium_paid_num",    transform: "toString" },
    ],
  },

  receivables: {
    id: "receivables",
    label: "Receivables",
    table: "receivables",
    icon: "📥",
    color: "#06b6d4",
    sheetName: "Receivables",
    columns: [
      { key: "party_name",      label: "Customer Name",    required: true,  type: "text",    example: "Acme Corp",        note: "Customer / debtor name" },
      { key: "invoice_number",  label: "Invoice Number",   required: true,  type: "text",    example: "INV-2026-001",     note: "" },
      { key: "currency",        label: "Currency",         required: true,  type: "text",    example: "USD",              note: "INR / USD / EUR etc." },
      { key: "amount",          label: "Invoice Amount",   required: true,  type: "text",    example: "250000",           note: "Stored as text in Database" },
      { key: "due_date",        label: "Due Date",         required: true,  type: "date",    example: "2026-04-30",       note: "YYYY-MM-DD" },
      { key: "status",          label: "Status",           required: true,  type: "text",    example: "Outstanding",      note: "Outstanding / Overdue / Partially Settled / Settled" },
      { key: "settled_amount",  label: "Settled Amount",   required: false, type: "numeric", example: "0",                note: "Leave 0 if not yet settled" },
      { key: "settled_date",    label: "Settled Date",     required: false, type: "date",    example: "",                 note: "YYYY-MM-DD — leave blank if unsettled" },
      { key: "notes",           label: "Notes",            required: false, type: "text",    example: "",                 note: "" },
    ],
    derived: [],
  },

  payables: {
    id: "payables",
    label: "Payables",
    table: "payables",
    icon: "📤",
    color: "#f59e0b",
    sheetName: "Payables",
    columns: [
      { key: "party_name",      label: "Supplier Name",    required: true,  type: "text",    example: "Tech Suppliers Ltd", note: "Vendor / supplier name" },
      { key: "invoice_number",  label: "Invoice Number",   required: true,  type: "text",    example: "VND-2026-045",       note: "" },
      { key: "currency",        label: "Currency",         required: true,  type: "text",    example: "USD",                note: "INR / USD / EUR etc." },
      { key: "amount",          label: "Invoice Amount",   required: true,  type: "text",    example: "180000",             note: "Stored as text in Database" },
      { key: "due_date",        label: "Due Date",         required: true,  type: "date",    example: "2026-04-15",         note: "YYYY-MM-DD" },
      { key: "status",          label: "Status",           required: true,  type: "text",    example: "Pending",            note: "Pending / Overdue / Partially Settled / Settled" },
      { key: "settled_amount",  label: "Settled Amount",   required: false, type: "numeric", example: "0",                  note: "Leave 0 if not yet settled" },
      { key: "settled_date",    label: "Settled Date",     required: false, type: "date",    example: "",                   note: "YYYY-MM-DD — leave blank if unsettled" },
      { key: "notes",           label: "Notes",            required: false, type: "text",    example: "",                   note: "" },
    ],
    derived: [],
  },

  cash: {
    id: "cash",
    label: "Cash in Bank",
    table: "cash_balances",
    icon: "🏦",
    color: "#10b981",
    sheetName: "Cash_Balances",
    columns: [
      { key: "bank_name",       label: "Bank Name",        required: true,  type: "text",    example: "HDFC Bank",   note: "" },
      { key: "account_number",  label: "Account Number",   required: false, type: "text",    example: "XXXX1234",    note: "" },
      { key: "currency",        label: "Currency",         required: true,  type: "text",    example: "INR",         note: "INR / USD / EUR etc." },
      { key: "current_balance", label: "Current Balance",  required: true,  type: "text",    example: "5000000",     note: "Stored as text in Database" },
      { key: "as_of_date",      label: "As of Date",       required: false, type: "date",    example: "2026-04-14",  note: "YYYY-MM-DD" },
    ],
    derived: [],
  },
};

// ── DATE PARSER ───────────────────────────────────────────────────────────────
function parseExcelDate(value) {
  if (!value && value !== 0) return null;

  // Excel serial number (e.g. 46382)
  if (typeof value === "number") {
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return date.toISOString().slice(0, 10);
  }

  const str = value.toString().trim();

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  // DD/MM/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split("/");
    return `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
  }

  // DD-MM-YYYY
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(str)) {
    const [d, m, y] = str.split("-");
    return `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
  }

  // "31 May 2026" or "31-May-2026" or any natural language date
  const parsed = new Date(str);
  if (!isNaN(parsed)) return parsed.toISOString().slice(0, 10);

  return null;
}

// ── TEMPLATE GENERATOR ────────────────────────────────────────────────────────
function generateTemplate(modId) {
  const mod = MODULES[modId];
  const wb = XLSX.utils.book_new();

  // Data sheet — header row + example row
  const headers = mod.columns.map(c => c.label + (c.required ? " *" : ""));
  const examples = mod.columns.map(c => c.example);
  const ws = XLSX.utils.aoa_to_sheet([headers, examples]);
  ws["!cols"] = mod.columns.map(c => ({ wch: Math.max(c.label.length + 6, 20) }));
  XLSX.utils.book_append_sheet(wb, ws, mod.sheetName);

  // Instructions sheet
  const rows = [
    [`NEBULA FINANCE — ${mod.label.toUpperCase()} UPLOAD TEMPLATE`],
    [""],
    ["INSTRUCTIONS"],
    ["1. Do NOT modify or delete Row 1 (column headers)."],
    ["2. Enter your data from Row 2 onwards."],
    ["3. Fields marked * are REQUIRED — upload will be rejected if blank."],
    ["4. All dates must be in YYYY-MM-DD format (e.g. 2026-04-14)."],
    ["5. Numeric fields must be plain numbers — no commas, currency symbols or spaces."],
    ["6. Save as .xlsx before uploading."],
    [""],
    ["FIELD REFERENCE"],
    ["Column", "Required", "Type", "Example", "Notes"],
    ...mod.columns.map(c => [
      c.label,
      c.required ? "YES ✓" : "Optional",
      c.type,
      c.example,
      c.note,
    ]),
  ];
  const wsI = XLSX.utils.aoa_to_sheet(rows);
  wsI["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 12 }, { wch: 22 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, wsI, "Instructions");

  XLSX.writeFile(wb, `Nebula_${mod.label.replace(/ /g, "_")}_Template.xlsx`);
}

// ── VALIDATOR ─────────────────────────────────────────────────────────────────
function validateRows(rows, modId) {
  const mod = MODULES[modId];
  const errors = [];
  const warnings = [];

  if (!rows || rows.length < 2) {
    return { errors: ["File appears empty — no data rows found."], warnings: [], data: [] };
  }

  // Build column index map from header row (strip * and trim)
  const headerRow = rows[0];
  const colMap = {};
  headerRow.forEach((h, i) => {
    const clean = String(h || "").replace(/\s*\*\s*$/, "").trim().toLowerCase();
    const col = mod.columns.find(c => c.label.toLowerCase() === clean);
    if (col) colMap[col.key] = i;
  });

  // Check all required columns exist
  const missingCols = mod.columns.filter(c => c.required && colMap[c.key] === undefined);
  if (missingCols.length > 0) {
    missingCols.forEach(c => errors.push(`Missing required column: "${c.label}"`));
    return { errors, warnings, data: [] };
  }

  const data = [];

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    // Skip fully empty rows
    if (!row || row.every(cell => cell === undefined || cell === null || String(cell).trim() === "")) continue;

    const record = {};
    const rowErrors = [];

    mod.columns.forEach(col => {
      const idx = colMap[col.key];
      const raw = idx !== undefined ? row[idx] : undefined;
      const val = (raw !== undefined && raw !== null) ? String(raw).trim() : "";

      if (col.required && val === "") {
        rowErrors.push(`Row ${r + 1}: "${col.label}" is required`);
        record[col.key] = null;
        return;
      }

      if (val === "") {
        record[col.key] = null;
        return;
      }

      if (col.type === "numeric") {
        const n = Number(val.replace(/,/g, ""));
        if (isNaN(n)) {
          rowErrors.push(`Row ${r + 1}: "${col.label}" must be a number (got "${val}")`);
          record[col.key] = null;
        } else {
          record[col.key] = n;
        }
   } else if (col.type === "date") {
        const idx2 = colMap[col.key];
        const rawCell = idx2 !== undefined ? rows[r][idx2] : undefined;
        const converted = parseExcelDate(rawCell);
        if (!converted && val !== "") {
          warnings.push(`Row ${r + 1}: "${col.label}" could not be parsed (got "${val}") — left blank`);
        }
        record[col.key] = converted;
      } else {
        // text — keep as string
        record[col.key] = val;
      }
    });

    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
      continue; // don't include invalid rows in data
    }

    // Apply derived fields (dual-column pattern in hedge)
    mod.derived.forEach(d => {
      if (record[d.source] !== null && record[d.source] !== undefined) {
        record[d.target] = String(record[d.source]);
      }
    });

    // Status defaults
    if (modId === "hedge" && !record.status) record.status = "Active";

    data.push(record);
  }

  return { errors, warnings, data };
}

// ── SUPABASE INSERT ───────────────────────────────────────────────────────────
async function insertRows(modId, data, companyId) {
  const mod = MODULES[modId];
  const rows = data.map(r => ({ ...r, company_id: companyId }));
  const { error } = await supabase.from(mod.table).insert(rows);
  if (error) throw new Error(error.message);
  return rows.length;
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function ExcelUpload({ onBack, companyId }) {
  const [selectedModule, setSelectedModule] = useState(null);
  const [isDragging, setIsDragging]         = useState(false);
  const [fileName, setFileName]             = useState(null);
  const [parseResult, setParseResult]       = useState(null);
  const [uploadState, setUploadState]       = useState("idle"); // idle | uploading | success | error
  const [uploadError, setUploadError]       = useState(null);
  const [uploadedCount, setUploadedCount]   = useState(0);
  const fileInputRef = useRef();

  const mod = selectedModule ? MODULES[selectedModule] : null;

  const resetUpload = () => {
    setFileName(null);
    setParseResult(null);
    setUploadState("idle");
    setUploadError(null);
    setUploadedCount(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleModuleSelect = (id) => {
    setSelectedModule(id);
    resetUpload();
  };

  const handleFile = useCallback((file) => {
    if (!selectedModule) return;
    setFileName(file.name);
    setParseResult(null);
    setUploadState("idle");
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        setParseResult(validateRows(rows, selectedModule));
      } catch (err) {
        setParseResult({ errors: [`Failed to parse file: ${err.message}`], warnings: [], data: [] });
      }
    };
    reader.readAsArrayBuffer(file);
  }, [selectedModule]);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleUpload = async () => {
    if (!parseResult?.data?.length || !companyId) return;
    setUploadState("uploading");
    setUploadError(null);
    try {
      const count = await insertRows(selectedModule, parseResult.data, companyId);
      setUploadedCount(count);
      setUploadState("success");
    } catch (err) {
      setUploadError(err.message);
      setUploadState("error");
    }
  };

  const canUpload = parseResult &&
    parseResult.errors.length === 0 &&
    parseResult.data.length > 0 &&
    uploadState === "idle";

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Excel Data Upload</h2>
          <p className="text-gray-400 text-sm">Download a template, fill in your data, and import directly into Nebula</p>
        </div>
      </div>

      {/* Step 1 — Module selector */}
      <div className="mb-6">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Step 1 — Select data type</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.values(MODULES).map(m => (
            <button
              key={m.id}
              onClick={() => handleModuleSelect(m.id)}
              className={`nebula-card p-5 text-left transition-all cursor-pointer group ${
                selectedModule === m.id ? "ring-2" : "hover:border-[#3a3d4a]"
              }`}
              style={selectedModule === m.id ? { "--tw-ring-color": m.color, borderColor: m.color + "60" } : {}}
            >
              <span className="text-2xl block mb-2">{m.icon}</span>
              <p className="text-sm font-semibold text-white">{m.label}</p>
              <p className="text-xs text-gray-500 mt-1">
                {m.columns.filter(c => c.required).length} required fields
              </p>
              {selectedModule === m.id && (
                <div className="mt-2 w-full h-0.5 rounded-full" style={{ background: m.color }} />
              )}
            </button>
          ))}
        </div>
      </div>

      {mod && (
        <>
          {/* Step 2 — Download template */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Step 2 — Download template</p>
            <div className="nebula-card p-5">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                    style={{ background: mod.color + "20" }}>
                    <FileSpreadsheet className="w-5 h-5" style={{ color: mod.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{mod.label} Template</p>
                    <p className="text-xs text-gray-500">
                      {mod.columns.filter(c => c.required).length} required · {mod.columns.filter(c => !c.required).length} optional · includes instructions sheet
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => generateTemplate(mod.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                  style={{ background: mod.color, color: "#fff" }}
                >
                  <Download className="w-4 h-4" /> Download .xlsx
                </button>
              </div>

              {/* Field reference table */}
              <div className="overflow-x-auto rounded-xl border border-[#2a2d3a]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#12141a] border-b border-[#2a2d3a]">
                      <th className="text-left text-gray-500 uppercase py-2.5 px-3 font-medium">Column</th>
                      <th className="text-left text-gray-500 uppercase py-2.5 px-3 font-medium">Required</th>
                      <th className="text-left text-gray-500 uppercase py-2.5 px-3 font-medium">Type</th>
                      <th className="text-left text-gray-500 uppercase py-2.5 px-3 font-medium">Example</th>
                      <th className="text-left text-gray-500 uppercase py-2.5 px-3 font-medium hidden md:table-cell">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mod.columns.map((c, i) => (
                      <tr key={c.key} className={`border-b border-[#2a2d3a]/40 ${i % 2 !== 0 ? "bg-[#12141a]/40" : ""}`}>
                        <td className="py-2 px-3 text-white font-medium">{c.label}</td>
                        <td className="py-2 px-3">
                          {c.required
                            ? <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-semibold">Required</span>
                            : <span className="px-2 py-0.5 rounded-full bg-[#1a1d26] text-gray-500">Optional</span>
                          }
                        </td>
                        <td className="py-2 px-3 text-indigo-400">{c.type}</td>
                        <td className="py-2 px-3 text-gray-400 font-mono">{c.example || "—"}</td>
                        <td className="py-2 px-3 text-gray-500 hidden md:table-cell">{c.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Step 3 — Upload */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Step 3 — Upload your file</p>

            {uploadState === "success" ? (
              <div className="nebula-card p-10 text-center border border-emerald-500/30">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-xl font-bold text-white mb-1">{uploadedCount} records uploaded</p>
                <p className="text-sm text-gray-400 mb-6">Your {mod.label} data is now live in Nebula Finance.</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={resetUpload}
                    className="px-5 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all"
                  >
                    Upload another file
                  </button>
                  <button
                    onClick={onBack}
                    className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                    style={{ background: mod.color }}
                  >
                    View {mod.label}
                  </button>
                </div>
              </div>
            ) : (
              <div className="nebula-card p-5">
                {/* Drop zone */}
                <div
                  onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current.click()}
                  className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
                    isDragging ? "border-indigo-500 bg-indigo-500/10" : "border-[#2a2d3a] hover:border-[#3a3d4a] bg-[#0a0b0f]"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }}
                  />
                  <Upload className={`w-8 h-8 mx-auto mb-3 ${isDragging ? "text-indigo-400" : "text-gray-600"}`} />
                  <p className="text-sm font-semibold text-white">Drop your Excel file here</p>
                  <p className="text-xs text-gray-500 mt-1">or click to browse — accepts .xlsx and .xls</p>
                </div>

                {/* File name pill */}
                {fileName && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-[#12141a] border border-[#2a2d3a]">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-sm text-white flex-1 truncate">{fileName}</span>
                    <button onClick={resetUpload} className="text-gray-500 hover:text-white transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Validation output */}
                {parseResult && (
                  <div className="mt-4 space-y-3">
                    {parseResult.errors.length > 0 && (
                      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                          <p className="text-sm font-semibold text-red-400">
                            {parseResult.errors.length} error{parseResult.errors.length > 1 ? "s" : ""} — fix before uploading
                          </p>
                        </div>
                        <ul className="space-y-1 pl-6 list-disc">
                          {parseResult.errors.map((e, i) => (
                            <li key={i} className="text-xs text-red-300">{e}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {parseResult.warnings.length > 0 && (
                      <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          <p className="text-sm font-semibold text-amber-400">
                            {parseResult.warnings.length} warning{parseResult.warnings.length > 1 ? "s" : ""}
                          </p>
                        </div>
                        <ul className="space-y-1 pl-6 list-disc">
                          {parseResult.warnings.map((w, i) => (
                            <li key={i} className="text-xs text-amber-300">{w}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {parseResult.data.length > 0 && parseResult.errors.length === 0 && (
                      <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <p className="text-sm font-semibold text-emerald-400">
                          {parseResult.data.length} valid row{parseResult.data.length > 1 ? "s" : ""} ready to upload
                        </p>
                      </div>
                    )}

                    {/* Preview table */}
                    {parseResult.data.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Preview — first 5 rows</p>
                        <div className="overflow-x-auto rounded-xl border border-[#2a2d3a]">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-[#12141a] border-b border-[#2a2d3a]">
                                {mod.columns.slice(0, 7).map(c => (
                                  <th key={c.key} className="text-left text-gray-500 py-2.5 px-3 font-medium whitespace-nowrap uppercase">
                                    {c.label}{c.required && <span className="text-red-400 ml-0.5">*</span>}
                                  </th>
                                ))}
                                {mod.columns.length > 7 && (
                                  <th className="text-left text-gray-500 py-2.5 px-3 font-medium">+{mod.columns.length - 7} more</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {parseResult.data.slice(0, 5).map((row, i) => (
                                <tr key={i} className={`border-b border-[#2a2d3a]/40 ${i % 2 !== 0 ? "bg-[#12141a]/40" : ""}`}>
                                  {mod.columns.slice(0, 7).map(c => (
                                    <td key={c.key} className="py-2 px-3 text-gray-300 whitespace-nowrap max-w-[120px] overflow-hidden text-ellipsis">
                                      {row[c.key] !== null && row[c.key] !== undefined ? String(row[c.key]).substring(0, 20) : <span className="text-gray-600">—</span>}
                                    </td>
                                  ))}
                                  {mod.columns.length > 7 && <td className="py-2 px-3 text-gray-600">…</td>}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {parseResult.data.length > 5 && (
                            <div className="px-3 py-2 bg-[#12141a] text-xs text-gray-500">
                              + {parseResult.data.length - 5} more rows
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload error */}
                {uploadState === "error" && uploadError && (
                  <div className="mt-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p className="text-sm text-red-400">Upload failed: {uploadError}</p>
                  </div>
                )}

                {/* Upload button */}
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleUpload}
                    disabled={!canUpload || uploadState === "uploading"}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
                    style={{
                      background: canUpload ? mod.color : "#1a1d26",
                      color: canUpload ? "#fff" : "#4b5563",
                      cursor: canUpload ? "pointer" : "not-allowed",
                    }}
                  >
                    {uploadState === "uploading" ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Uploading…
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload {parseResult?.data?.length || 0} records to Nebula
                      </>
                    )}
                  </button>
                  {fileName && (
                    <button onClick={resetUpload} className="px-4 py-2.5 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {!selectedModule && (
        <div className="nebula-card p-16 text-center text-gray-600">
          <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select a data type above to get started</p>
        </div>
      )}
    </div>
  );
}
